import { useMemo } from 'react';
import { Client, useQuery } from 'urql';
import {
  DatabaseLookupType,
  GetCompanyDatabaseCellValueDocument,
  GetCompanyDatabaseCellValueQuery,
  LookupCompanyDatabaseValueDocument,
  LookupCompanyDatabaseValueQuery,
  OrderDeepFragment,
  QuantityEnum,
} from '../gql/graphql';
import { ResolvedQuantity } from '../domain/property-types';

/**
 * Type for pre-resolved database lookups.
 * Key is generated by `getDatabaseLookupKey`.
 */
export type DatabaseLookupMap = Map<
  string,
  { value: string | null; found: boolean }
>;

/**
 * Global cache for database lookup results
 * Key: lookup key (from getDatabaseLookupKey)
 * Value: { value: string | null, found: boolean }
 * This cache persists across component re-renders to avoid re-running LLM lookups
 */
const databaseLookupCache = new Map<
  string,
  { value: string | null; found: boolean }
>();

/**
 * Global cache for row IDs from database lookups
 * Key: row key (databaseId:searchColumnId:searchTerm:profileId) - WITHOUT valueColumnId
 * Value: rowId string
 * This allows multiple columns to reference the same lookup result
 */
const rowIdCache = new Map<string, string>();

/**
 * Clears all database lookup caches.
 * Call this when switching projects or when database content may have changed.
 */
export function clearDatabaseLookupCaches(): void {
  databaseLookupCache.clear();
  rowIdCache.clear();
}

/**
 * Clears cache entries for a specific database.
 * Call this when a specific database's content has been updated.
 */
export function clearDatabaseLookupCacheForDatabase(databaseId: string): void {
  // Clear entries from databaseLookupCache that match the databaseId
  for (const key of databaseLookupCache.keys()) {
    if (key.startsWith(`${databaseId}:`)) {
      databaseLookupCache.delete(key);
    }
  }
  // Clear entries from rowIdCache that match the databaseId
  for (const key of rowIdCache.keys()) {
    if (key.startsWith(`${databaseId}:`)) {
      rowIdCache.delete(key);
    }
  }
}

/**
 * Generates a row key (without valueColumnId) for row caching.
 * Multiple columns with same inputs but different valueColumnId share the same row.
 */
export function getRowKey(
  lookup: DatabaseLookupType | null | undefined
): string | null {
  if (!lookup?.databaseId || !lookup?.searchColumnId || !lookup?.searchTerm) {
    return null;
  }
  const profileId = lookup.lookupProfileId ?? 'none';
  return `${lookup.databaseId}:${lookup.searchColumnId}:${lookup.searchTerm}:${profileId}`;
}

/**
 * Seeds the database lookup cache with pre-resolved values.
 * This is called when entries are loaded from backend cache to avoid re-running LLM lookups.
 */
export function seedDatabaseLookupCache(
  key: string,
  value: { value: string | null; found: boolean }
): void {
  if (!databaseLookupCache.has(key)) {
    databaseLookupCache.set(key, value);
  }
}

/**
 * Scope for evaluating dynamic search terms.
 * Contains entry properties and resolved column values.
 */
export type DynamicSearchTermScope = {
  name: string;
  columnValues: Map<string, ResolvedQuantity['value']>;
};

/**
 * Function type for resolving dynamic lookups.
 * Called during resolution when a search term needs evaluation.
 */
export type DynamicLookupResolver = (
  databaseId: string,
  valueColumnId: string,
  searchColumnId: string,
  evaluatedSearchTerm: string,
  lookupProfileId: string | null
) => Promise<{ value: string | null; found: boolean }>;

/**
 * Checks if a search term is dynamic (contains variable references).
 * Dynamic terms start with "var_" which indicates they reference columns.
 */
export function isDynamicSearchTerm(
  searchTerm: string | null | undefined
): boolean {
  if (!searchTerm) return false;
  // Check for var_ prefix (column reference) or $name (entry name reference)
  return searchTerm.includes('var_') || searchTerm === '$name';
}

/**
 * Evaluates a dynamic search term using the provided scope.
 * Returns the resolved string value to use for the database lookup.
 */
export function evaluateDynamicSearchTerm(
  searchTerm: string,
  scope: DynamicSearchTermScope
): string | null {
  // Handle special $name reference
  if (searchTerm === '$name') {
    return scope.name;
  }

  // Handle column references (var_xxx format)
  // For now, we support simple single-variable references
  const varMatch = searchTerm.match(/^var_[\w_]+$/);
  if (varMatch) {
    const value = scope.columnValues.get(searchTerm);
    if (value === null || value === undefined) {
      return null;
    }
    // Convert to string for search
    return String(value);
  }

  // If it's a more complex expression, return null for now
  // (we can add mathjs evaluation later if needed)
  return null;
}

/**
 * Generates a unique key for a database lookup configuration.
 */
export function getDatabaseLookupKey(
  lookup: DatabaseLookupType | null | undefined
): string | null {
  if (
    !lookup?.databaseId ||
    !lookup?.searchColumnId ||
    !lookup?.valueColumnId ||
    !lookup?.searchTerm
  ) {
    return null;
  }
  // Include lookupProfileId in the key to differentiate cached results
  const profileId = lookup.lookupProfileId ?? 'none';
  return `${lookup.databaseId}:${lookup.valueColumnId}:${lookup.searchColumnId}:${lookup.searchTerm}:${profileId}`;
}

/**
 * Extracts all unique static DATABASE_LOOKUP configurations from orders.
 * Dynamic lookups (with variable references) are skipped and resolved during entry resolution.
 */
// eslint-disable-next-line complexity
export function extractDatabaseLookups(
  orders: Array<OrderDeepFragment>
): DatabaseLookupType[] {
  const lookups: DatabaseLookupType[] = [];
  const seenKeys = new Set<string>();

  for (const order of orders) {
    for (const entry of order.orderEntries) {
      // Check main quantity
      if (
        entry.quantity?.type === QuantityEnum.DatabaseLookup &&
        entry.quantity.databaseLookup
      ) {
        // Skip dynamic lookups - they'll be resolved during entry resolution
        if (!isDynamicSearchTerm(entry.quantity.databaseLookup.searchTerm)) {
          const key = getDatabaseLookupKey(entry.quantity.databaseLookup);
          if (key && !seenKeys.has(key)) {
            seenKeys.add(key);
            lookups.push(entry.quantity.databaseLookup);
          }
        }
      }

      // Check custom fields
      for (const cf of entry.customFields ?? []) {
        if (
          cf.value?.type === QuantityEnum.DatabaseLookup &&
          cf.value.databaseLookup
        ) {
          // Skip dynamic lookups
          if (!isDynamicSearchTerm(cf.value.databaseLookup.searchTerm)) {
            const key = getDatabaseLookupKey(cf.value.databaseLookup);
            if (key && !seenKeys.has(key)) {
              seenKeys.add(key);
              lookups.push(cf.value.databaseLookup);
            }
          }
        }
      }
    }
  }

  return lookups;
}

type LookupResult = { value: string | null; found: boolean };

/**
 * Helper: Fetch a single cell value by rowId
 */
async function fetchCellValue(
  client: Client,
  rowId: string,
  columnId: string
): Promise<string | null> {
  const cellResult = await client
    .query<GetCompanyDatabaseCellValueQuery>(
      GetCompanyDatabaseCellValueDocument,
      { rowId, columnId }
    )
    .toPromise();
  return cellResult.data?.getCompanyDatabaseCellValue ?? null;
}

/**
 * Helper: Store result in caches and results map
 */
function cacheResult(
  key: string,
  result: LookupResult,
  results: DatabaseLookupMap
): void {
  databaseLookupCache.set(key, result);
  results.set(key, result);
}

/**
 * Helper: Process additional columns in a lookup group using rowId
 */
async function fetchAdditionalColumns(
  client: Client,
  items: Array<{ lookup: DatabaseLookupType; key: string }>,
  rowId: string,
  found: boolean,
  results: DatabaseLookupMap
): Promise<void> {
  await Promise.all(
    items.map(async (item) => {
      try {
        if (!item.lookup.valueColumnId) {
          cacheResult(item.key, { value: null, found: false }, results);
          return;
        }
        const cellValue = await fetchCellValue(
          client,
          rowId,
          item.lookup.valueColumnId
        );
        const cellResult = { value: cellValue, found };
        cacheResult(item.key, cellResult, results);
      } catch {
        cacheResult(item.key, { value: null, found: false }, results);
      }
    })
  );
}

/**
 * Helper: Mark all items in a group as failed
 */
function markGroupFailed(
  items: Array<{ key: string }>,
  results: DatabaseLookupMap
): void {
  for (const item of items) {
    cacheResult(item.key, { value: null, found: false }, results);
  }
}

/**
 * Helper: Run single LLM lookup query
 */
async function runLookupQuery(
  client: Client,
  lookup: DatabaseLookupType
): Promise<
  LookupCompanyDatabaseValueQuery['lookupCompanyDatabaseValue'] | null
> {
  const result = await client
    .query<LookupCompanyDatabaseValueQuery>(
      LookupCompanyDatabaseValueDocument,
      {
        databaseId: lookup.databaseId,
        searchColumnId: lookup.searchColumnId,
        searchTerm: lookup.searchTerm,
        valueColumnId: lookup.valueColumnId,
        lookupProfileId: lookup.lookupProfileId ?? null,
      }
    )
    .toPromise();

  if (result.error || !result.data?.lookupCompanyDatabaseValue) {
    return null;
  }
  return result.data.lookupCompanyDatabaseValue;
}

/**
 * Helper: Process a single lookup group (one LLM call + additional columns)
 */
async function processLookupGroup(
  client: Client,
  rowKey: string,
  items: Array<{ lookup: DatabaseLookupType; key: string; rowKey: string }>,
  results: DatabaseLookupMap
): Promise<void> {
  const { lookup, key } = items[0];
  try {
    const lookupData = await runLookupQuery(client, lookup);
    if (!lookupData) {
      markGroupFailed(items, results);
      return;
    }

    const { value, found, rowId } = lookupData;
    if (rowId) rowIdCache.set(rowKey, rowId);

    cacheResult(key, { value: value ?? null, found }, results);

    if (rowId && items.length > 1) {
      await fetchAdditionalColumns(
        client,
        items.slice(1),
        rowId,
        found,
        results
      );
    }
  } catch {
    markGroupFailed(items, results);
  }
}

/**
 * Batch-resolves all database lookups and returns a map.
 * Uses row caching - multiple columns with same inputs (except valueColumnId) share one LLM call.
 */
export async function batchResolveDatabaseLookups(
  client: Client,
  lookups: DatabaseLookupType[]
): Promise<DatabaseLookupMap> {
  const results: DatabaseLookupMap = new Map();

  // Categorize lookups
  const lookupsNeedingCellFetch: Array<{
    lookup: DatabaseLookupType;
    key: string;
    rowId: string;
  }> = [];
  const lookupsNeedingFullLookup: Array<{
    lookup: DatabaseLookupType;
    key: string;
    rowKey: string;
  }> = [];

  for (const lookup of lookups) {
    const key = getDatabaseLookupKey(lookup);
    const rowKey = getRowKey(lookup);
    if (!key || !rowKey) continue;

    const cached = databaseLookupCache.get(key);
    if (cached) {
      results.set(key, cached);
      continue;
    }

    const cachedRowId = rowIdCache.get(rowKey);
    if (cachedRowId) {
      lookupsNeedingCellFetch.push({ lookup, key, rowId: cachedRowId });
    } else {
      lookupsNeedingFullLookup.push({ lookup, key, rowKey });
    }
  }

  // Group by rowKey
  const rowKeyToLookups = new Map<
    string,
    Array<{ lookup: DatabaseLookupType; key: string; rowKey: string }>
  >();
  for (const item of lookupsNeedingFullLookup) {
    const existing = rowKeyToLookups.get(item.rowKey) ?? [];
    existing.push(item);
    rowKeyToLookups.set(item.rowKey, existing);
  }

  // Run LLM lookups (one per rowKey)
  const fullLookupPromises = Array.from(rowKeyToLookups.entries()).map(
    ([rowKey, items]) => processLookupGroup(client, rowKey, items, results)
  );

  // Fetch cells for cached rowIds
  const cellFetchPromises = lookupsNeedingCellFetch.map(
    async ({ lookup, key, rowId }) => {
      try {
        if (!lookup.valueColumnId) {
          cacheResult(key, { value: null, found: false }, results);
          return;
        }
        const cellValue = await fetchCellValue(
          client,
          rowId,
          lookup.valueColumnId
        );
        cacheResult(key, { value: cellValue, found: true }, results);
      } catch {
        cacheResult(key, { value: null, found: false }, results);
      }
    }
  );

  await Promise.all([...fullLookupPromises, ...cellFetchPromises]);
  return results;
}

/**
 * Helper: Try to get value from cached rowId
 */
async function tryGetFromCachedRowId(
  client: Client,
  cachedRowId: string,
  valueColumnId: string
): Promise<LookupResult | null> {
  try {
    const cellValue = await fetchCellValue(client, cachedRowId, valueColumnId);
    return { value: cellValue, found: true };
  } catch {
    return null;
  }
}

/**
 * Helper: Do full LLM lookup
 */
async function doFullLookup(
  client: Client,
  databaseId: string,
  searchColumnId: string,
  searchTerm: string,
  valueColumnId: string,
  lookupProfileId: string | null
): Promise<{ result: LookupResult; rowId: string | null }> {
  const queryResult = await client
    .query<LookupCompanyDatabaseValueQuery>(
      LookupCompanyDatabaseValueDocument,
      {
        databaseId,
        searchColumnId,
        searchTerm,
        valueColumnId,
        lookupProfileId,
      }
    )
    .toPromise();

  if (queryResult.error || !queryResult.data?.lookupCompanyDatabaseValue) {
    return { result: { value: null, found: false }, rowId: null };
  }

  const { value, found, rowId } = queryResult.data.lookupCompanyDatabaseValue;
  return { result: { value: value ?? null, found }, rowId: rowId ?? null };
}

/**
 * Creates a DynamicLookupResolver function that queries the database.
 * Uses row caching so multiple columns with same inputs share one LLM call.
 */
/**
 * Helper to get value from a cached row ID and update caches
 */
async function getValueFromRowId(
  client: Client,
  rowId: string,
  valueColumnId: string,
  cacheKey: string,
  localCache: Map<string, LookupResult>
): Promise<LookupResult | null> {
  const fromRow = await tryGetFromCachedRowId(client, rowId, valueColumnId);
  if (fromRow) {
    localCache.set(cacheKey, fromRow);
    databaseLookupCache.set(cacheKey, fromRow);
    return fromRow;
  }
  return null;
}

/**
 * State container for the dynamic lookup resolver
 */
type ResolverState = {
  localCache: Map<string, LookupResult>;
  localRowIdCache: Map<string, string>;
  inFlightRequests: Map<string, Promise<LookupResult>>;
  inFlightRowLookups: Map<string, Promise<string | null>>;
};

/**
 * Try to get rowId from caches or wait for in-flight lookup
 */
async function tryGetCachedRowId(
  state: ResolverState,
  rowKey: string
): Promise<string | undefined> {
  // Check local and global caches
  const cached = state.localRowIdCache.get(rowKey) ?? rowIdCache.get(rowKey);
  if (cached) return cached;

  // Check if there's an in-flight row lookup we can wait for
  const inFlightRowLookup = state.inFlightRowLookups.get(rowKey);
  if (inFlightRowLookup) {
    return (await inFlightRowLookup) ?? undefined;
  }
  return undefined;
}

/**
 * Perform the full row lookup and cache the result
 */
async function performRowLookup(
  client: Client,
  state: ResolverState,
  rowKey: string,
  databaseId: string,
  searchColumnId: string,
  evaluatedSearchTerm: string,
  valueColumnId: string,
  lookupProfileId: string | null
): Promise<string | null> {
  try {
    const { rowId } = await doFullLookup(
      client,
      databaseId,
      searchColumnId,
      evaluatedSearchTerm,
      valueColumnId,
      lookupProfileId
    );
    if (rowId) {
      state.localRowIdCache.set(rowKey, rowId);
      rowIdCache.set(rowKey, rowId);
    }
    return rowId;
  } catch {
    return null;
  }
}

/**
 * Core lookup logic - either uses cached rowId or initiates new lookup
 */
async function executeLookup(
  client: Client,
  state: ResolverState,
  cacheKey: string,
  rowKey: string,
  databaseId: string,
  searchColumnId: string,
  evaluatedSearchTerm: string,
  valueColumnId: string,
  lookupProfileId: string | null
): Promise<LookupResult> {
  // Try to get a cached rowId
  const cachedRowId = await tryGetCachedRowId(state, rowKey);
  if (cachedRowId) {
    const result = await getValueFromRowId(
      client,
      cachedRowId,
      valueColumnId,
      cacheKey,
      state.localCache
    );
    if (result) return result;
  }

  // Need to do full lookup - check if another column is already doing this
  let rowLookupPromise = state.inFlightRowLookups.get(rowKey);
  let weOwnTheRowLookup = false;

  if (!rowLookupPromise) {
    weOwnTheRowLookup = true;
    rowLookupPromise = performRowLookup(
      client,
      state,
      rowKey,
      databaseId,
      searchColumnId,
      evaluatedSearchTerm,
      valueColumnId,
      lookupProfileId
    );
    state.inFlightRowLookups.set(rowKey, rowLookupPromise);
  }

  try {
    const rowId = await rowLookupPromise;
    if (rowId) {
      const result = await getValueFromRowId(
        client,
        rowId,
        valueColumnId,
        cacheKey,
        state.localCache
      );
      if (result) return result;
    }
    // No match found
    const errorResult = { value: null, found: false };
    state.localCache.set(cacheKey, errorResult);
    databaseLookupCache.set(cacheKey, errorResult);
    return errorResult;
  } finally {
    if (weOwnTheRowLookup) {
      state.inFlightRowLookups.delete(rowKey);
    }
  }
}

export function createDynamicLookupResolver(
  client: Client
): DynamicLookupResolver {
  const state: ResolverState = {
    localCache: new Map(),
    localRowIdCache: new Map(),
    inFlightRequests: new Map(),
    inFlightRowLookups: new Map(),
  };

  return async (
    databaseId: string,
    valueColumnId: string,
    searchColumnId: string,
    evaluatedSearchTerm: string,
    lookupProfileId: string | null
  ) => {
    const profileKey = lookupProfileId ?? 'none';
    const cacheKey = `${databaseId}:${valueColumnId}:${searchColumnId}:${evaluatedSearchTerm}:${profileKey}`;
    const rowKey = `${databaseId}:${searchColumnId}:${evaluatedSearchTerm}:${profileKey}`;

    // Check caches first
    const localCached = state.localCache.get(cacheKey);
    if (localCached) return localCached;

    const globalCached = databaseLookupCache.get(cacheKey);
    if (globalCached) {
      state.localCache.set(cacheKey, globalCached);
      return globalCached;
    }

    // Check if there's already an in-flight request for this exact key
    const inFlight = state.inFlightRequests.get(cacheKey);
    if (inFlight) return inFlight;

    // Create and track the lookup promise
    const lookupPromise = executeLookup(
      client,
      state,
      cacheKey,
      rowKey,
      databaseId,
      searchColumnId,
      evaluatedSearchTerm,
      valueColumnId,
      lookupProfileId
    );
    state.inFlightRequests.set(cacheKey, lookupPromise);

    try {
      return await lookupPromise;
    } finally {
      state.inFlightRequests.delete(cacheKey);
    }
  };
}

/**
 * Hook to resolve a database lookup value.
 * Returns the looked-up value or null if not found.
 */
export function useDatabaseLookup(
  databaseLookup: DatabaseLookupType | null | undefined
) {
  const shouldQuery = Boolean(
    databaseLookup?.databaseId &&
      databaseLookup?.searchColumnId &&
      databaseLookup?.valueColumnId &&
      databaseLookup?.searchTerm
  );

  const [{ data, fetching, error }] = useQuery({
    query: LookupCompanyDatabaseValueDocument,
    variables: {
      databaseId: databaseLookup?.databaseId ?? '',
      searchColumnId: databaseLookup?.searchColumnId ?? '',
      searchTerm: databaseLookup?.searchTerm ?? '',
      valueColumnId: databaseLookup?.valueColumnId ?? '',
      lookupProfileId: databaseLookup?.lookupProfileId ?? null,
    },
    pause: !shouldQuery,
  });

  const result = useMemo(() => {
    if (!shouldQuery) {
      return { value: null, found: false, loading: false, error: null };
    }
    if (fetching) {
      return { value: null, found: false, loading: true, error: null };
    }
    if (error) {
      return { value: null, found: false, loading: false, error };
    }
    const lookupResult = data?.lookupCompanyDatabaseValue;
    if (!lookupResult) {
      return { value: null, found: false, loading: false, error: null };
    }
    return {
      value: lookupResult.value ?? null,
      found: lookupResult.found,
      loading: false,
      error: null,
    };
  }, [shouldQuery, fetching, error, data]);

  return result;
}
